import { SelectOption } from '@/core/types/SelectOption.ts'
import { mapToOptions, OptionMapped } from '@/core/utils/general.ts'
import {
  Rentability,
  RentabilityType
} from '@/modules/settings/tasa/types/types.ts'
import useHandleAPIError from '@/core/hooks/useHandleAPIError.ts'
import { useQuery } from '@tanstack/react-query'
import selectServices from '@/core/services/selectServices.ts'
import { useEffect, useMemo } from 'react'
import rentabilityService from '@/modules/settings/tasa/services/RentabilityService.ts'
import {
  processRentabilities,
  sortDesc
} from '@/core/hooks/useRentabilityLoaderAssign/processRentabilities.ts'

type RentabilityMappedLoader = {
  options: SelectOption<OptionMapped<Rentability>>[]
  isLoading: boolean
}

type RentabilityObj = Pick<Rentability, 'quotes' | 'timesQuotes'>

const useRentabilityLoaderAssign = (
  levelRentabilityId?: string | number,
  promoRentabilityId?: string | number,
  rentabilityType: RentabilityType = 'PRODUCT'
): {
  rentabilityLoader: RentabilityMappedLoader
} => {
  const { handleAPIError } = useHandleAPIError()

  const {
    data: defaultRentabilities,
    isLoading: isLoadingDefaultRentabilities,
    error: errorDefaultRentabilities
  } = useQuery({
    queryKey: ['getDefaultRentabilities', rentabilityType],
    queryFn: () => selectServices.getRentabilitiesByType(rentabilityType)
  })

  useEffect(() => {
    if (errorDefaultRentabilities)
      handleAPIError(
        errorDefaultRentabilities,
        'Error cargando lista de rentabilidades por defecto'
      )
  }, [errorDefaultRentabilities, handleAPIError])

  const {
    data: allRentabilities,
    isFetching: isLoadingAllRentabilities,
    error: errorAllRentabilities
  } = useQuery({
    queryKey: ['getAllRentabilities', rentabilityType],
    queryFn: () =>
      rentabilityService.getRentabilities(`?limit=50&type=${rentabilityType}`)
  })

  useEffect(() => {
    if (errorAllRentabilities)
      handleAPIError(
        errorAllRentabilities,
        'Error cargando lista de rentabilidades completa'
      )
  }, [errorAllRentabilities, handleAPIError])

  const finalRentabilityLoader = useMemo((): RentabilityMappedLoader => {
    const defaultRentabilitiesReady =
      defaultRentabilities && !isLoadingDefaultRentabilities
    const fullRentabilitiesReady =
      allRentabilities && !isLoadingAllRentabilities

    const defaultResp = {
      options: [],
      isLoading: isLoadingDefaultRentabilities || isLoadingAllRentabilities
    }

    if (!defaultRentabilitiesReady || !fullRentabilitiesReady) {
      return defaultResp
    }

    const mapFn = (obj: RentabilityObj) =>
      `${obj.quotes} cuotas - ${obj.timesQuotes} días`

    if (!promoRentabilityId && !levelRentabilityId) {
      return {
        options: defaultRentabilities
          ? mapToOptions(defaultRentabilities.sort(sortDesc), mapFn, 'id')
          : [],
        isLoading: isLoadingDefaultRentabilities
      }
    }

    levelRentabilityId = levelRentabilityId
      ? String(levelRentabilityId)
      : undefined
    promoRentabilityId = promoRentabilityId
      ? String(promoRentabilityId)
      : undefined

    const result = processRentabilities(
      allRentabilities.results,
      levelRentabilityId,
      promoRentabilityId,
      rentabilityType
    )

    return {
      options: result ? mapToOptions(result, mapFn, 'id') : [],
      isLoading: isLoadingAllRentabilities
    }
  }, [
    allRentabilities,
    isLoadingAllRentabilities,
    levelRentabilityId,
    promoRentabilityId,
    isLoadingDefaultRentabilities,
    defaultRentabilities,
    rentabilityType
  ])

  return {
    rentabilityLoader: finalRentabilityLoader
  }
}

export default useRentabilityLoaderAssign
